import { useCompanyContext } from '@/lib/providers/CompanyProvider'
import { useAuthStore } from '@/lib/stores/authStore'
import { Bars3Icon } from '@heroicons/react/24/solid'
import { useClickOutside, useMediaQuery } from '@mantine/hooks'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useMemo, useRef, useState } from 'react'
import OutlineButton from '../Button/OutlineButton'
import MyMenuDesktop from './MyMenuDesktop'
import MyMenuMobile from './MyMenuMobile'
import { useTranslation } from 'react-i18next'

const Header = ({ children = null }: { children?: React.ReactNode }) => {
  const router = useRouter()
  const { company_logo, type } = useCompanyContext()
  const { user } = useAuthStore()
  const isLaptop = useMediaQuery('(min-width:1024px)')

  const [isOpenDropdown, setIsOpenDropdown] = useState(false)

  const menuRef = useRef<HTMLDivElement>(null)
  const dropDownRef = useClickOutside(
    () => {
      setIsOpenDropdown(false)
    },
    null,
    [menuRef.current],
  )

  const isSigned = !!user
  const { t } = useTranslation('common')

  const goToSignIn = () => {
    router.push('/login')
  }

  const handleMouseEnter = () => {
    if (isLaptop) {
      setIsOpenDropdown(true)
    }
  }

  const handleMouseLeave = () => {
    if (isLaptop) {
      setIsOpenDropdown(false)
    }
  }

  const rootPath = useMemo(() => {
    if (type === 'lecture') {
      return '/my'
    } else {
      return '/'
    }
  }, [type])

  return (
    <div className="sticky top-0 z-50 w-full">
      <div className="flex h-[60px] w-full flex-none items-center justify-between border-b border-gray-200 bg-white px-4 py-2 md:px-8">
        {/* Logo */}
        {company_logo?.file_path ? (
          <Link href={rootPath} className="flex h-full items-center">
            <Image
              src={company_logo?.file_path}
              alt="header-logo"
              className="h-full w-auto object-contain"
              width={154}
              height={55}
              style={{ height: '100%', width: 'auto' }}
            />
          </Link>
        ) : (
          <div></div>
        )}

        {/* Right Side */}
        <div
          className="relative flex items-center justify-center"
          ref={menuRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isSigned ? (
            <>
              <button onClick={() => setIsOpenDropdown(!isOpenDropdown)}>
                <Bars3Icon className="h-6 w-6" />
              </button>
              {isOpenDropdown && isLaptop && <MyMenuDesktop ref={dropDownRef} />}
              {isOpenDropdown && !isLaptop && (
                <MyMenuMobile setIsOpenDropdown={setIsOpenDropdown} />
              )}
            </>
          ) : (
            <OutlineButton
              variant="assistive"
              className="!h-8 !px-[14px]"
              size="sm"
              onClick={goToSignIn}
            >
              <span className="text-sm font-semibold text-label-normal">{t('auth.login')}</span>
            </OutlineButton>
          )}
        </div>
      </div>
      {children}
    </div>
  )
}

export default Header
